import { FormEvent, useEffect, useState } from "react";
import { supabase } from "./lib/supabase";
import "./platform-support-center.css";

type Ticket = {
  id: string;
  organization_id: string;
  public_reference: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  updated_at: string;
};
type Message = {
  id: number;
  ticket_id: string;
  author_side: "stand" | "support";
  body: string;
  created_at: string;
};
const statusLabel: Record<string, string> = {
  open: "Aberto",
  waiting_stand: "Aguardar stand",
  waiting_support: "Aguardar suporte",
  resolved: "Resolvido",
  closed: "Encerrado",
};

export function PlatformSupportCenter() {
  const [tickets, setTickets] = useState<Ticket[]>([]),
    [messages, setMessages] = useState<Message[]>([]),
    [selected, setSelected] = useState<string | null>(null),
    [filter, setFilter] = useState("active"),
    [notice, setNotice] = useState<string | null>(null),
    [busy, setBusy] = useState(false);
  const load = async () => {
    const [ticketResult, messageResult] = await Promise.all([
      supabase
        .from("stand_support_tickets")
        .select(
          "id,organization_id,public_reference,subject,category,priority,status,updated_at",
        )
        .order("updated_at", { ascending: false }),
      supabase
        .from("stand_support_messages")
        .select("id,ticket_id,author_side,body,created_at")
        .order("created_at"),
    ]);
    const error = ticketResult.error ?? messageResult.error;
    if (error) return setNotice(error.message);
    setTickets((ticketResult.data ?? []) as Ticket[]);
    setMessages((messageResult.data ?? []) as Message[]);
  };
  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, []);
  const visible = tickets.filter(
      (ticket) =>
        filter === "all" ||
        (filter === "active"
          ? !["resolved", "closed"].includes(ticket.status)
          : ticket.status === filter),
    ),
    active = tickets.find((ticket) => ticket.id === selected) ?? null;
  const reply = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!active) return;
    const form = event.currentTarget,
      data = new FormData(form);
    setBusy(true);
    setNotice("A guardar a resposta…");
    const { error } = await supabase.rpc("reply_stand_support_ticket", {
      p_ticket_id: active.id,
      p_message: String(data.get("message") || ""),
      p_status: String(data.get("status") || "waiting_stand"),
    });
    setBusy(false);
    if (error) return setNotice(error.message);
    form.reset();
    setNotice(
      "Resposta guardada, notificada ao stand e refletida na caixa de email de Testes.",
    );
    await load();
  };
  return (
    <section className="platform-support-center">
      <div className="platform-support-toolbar">
        <div>
          <strong>Pedidos de suporte</strong>
          <small>
            Somente utilizadores com a permissão Atender suporte conseguem
            consultar este histórico.
          </small>
        </div>
        <label>
          <span>Estado</span>
          <select
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          >
            <option value="active">Em tratamento</option>
            <option value="all">Todos</option>
            <option value="open">Abertos</option>
            <option value="waiting_support">Aguardar suporte</option>
            <option value="waiting_stand">Aguardar stand</option>
            <option value="resolved">Resolvidos</option>
          </select>
        </label>
      </div>
      {notice && (
        <button
          className="platform-support-notice"
          onClick={() => setNotice(null)}
        >
          {notice} ×
        </button>
      )}
      <div className="platform-support-layout">
        <div className="platform-support-list">
          {visible.length === 0 ? (
            <p>Não existem pedidos neste filtro.</p>
          ) : (
            visible.map((ticket) => (
              <button
                className={selected === ticket.id ? "active" : ""}
                key={ticket.id}
                onClick={() => setSelected(ticket.id)}
              >
                <span>
                  <strong>{ticket.subject}</strong>
                  <small>
                    {ticket.public_reference} · {statusLabel[ticket.status]}
                  </small>
                  <time>
                    {new Date(ticket.updated_at).toLocaleString("pt-PT")}
                  </time>
                </span>
                <em className={ticket.priority}>
                  {ticket.priority === "urgent"
                    ? "Urgente"
                    : ticket.priority === "high"
                      ? "Alta"
                      : "Normal"}
                </em>
              </button>
            ))
          )}
        </div>
        {active ? (
          <article className="platform-support-thread">
            <header>
              <div>
                <strong>{active.subject}</strong>
                <small>
                  {active.public_reference} · Organização{" "}
                  {active.organization_id.slice(0, 8)}
                </small>
              </div>
              <span>{statusLabel[active.status]}</span>
            </header>
            <div>
              {messages
                .filter((message) => message.ticket_id === active.id)
                .map((message) => (
                  <article className={message.author_side} key={message.id}>
                    <p>{message.body}</p>
                    <time>
                      {new Date(message.created_at).toLocaleString("pt-PT")}
                    </time>
                  </article>
                ))}
            </div>
            {!["resolved", "closed"].includes(active.status) && (
              <form onSubmit={reply}>
                <textarea
                  name="message"
                  rows={4}
                  required
                  placeholder="Escreva a resposta…"
                />
                <label>
                  <span>Depois de responder</span>
                  <select name="status">
                    <option value="waiting_stand">
                      Aguardar resposta do stand
                    </option>
                    <option value="resolved">Marcar como resolvido</option>
                  </select>
                </label>
                <button disabled={busy}>Guardar</button>
              </form>
            )}
          </article>
        ) : (
          <div className="platform-support-empty">
            Escolha um pedido para consultar o histórico.
          </div>
        )}
      </div>
    </section>
  );
}
